import {
	type Component,
	type ComponentProps,
	For,
	Match,
	onCleanup,
	onMount,
	Show,
	Switch
} from "solid-js";
import { Input } from "~/solidJs/shared";
import type { Coordinates } from "~/solidJs/shared/helpers/types/ISizeble";
import { Menu } from "~/solidJs/shared/ui/molecules/Menu";
import { useEnumContextMenu } from "./methods";

interface EnumContextMenuComponentProps {
	isOpen: () => boolean;
	setIsOpen: (value: boolean) => void;
	position: () => Coordinates;
	socketName: () => string | null;
	nodeUuid: () => string | null;
	searchQuery: () => string;
	onSearchInput: (value: string) => void;
	actions: () => Menu.ClosedMenuItem[];
}

// Invisible trigger component positioned at click coordinates
const InvisibleTrigger: Component<ComponentProps<"button">> = (props) => {
	return (
		<button
			{...props}
			class="pointer-events-none opacity-0"
			style={{
				position: "absolute",
				left: "0",
				top: "0",
				width: "1px",
				height: "1px"
			}}
		/>
	);
};

export const EnumContextMenu = (props: EnumContextMenuComponentProps) => {
	let contentRef: HTMLDivElement;
	let inputRef: HTMLInputElement;
	const MenuNavigationEvents = new Set([
		"ArrowRight",
		"ArrowLeft",
		"ArrowUp",
		"ArrowDown",
		"Enter",
		"Escape"
	]);
	const preventScroll = (e: WheelEvent) => {
		if (e.ctrlKey) {
			e.preventDefault();
		}
		e.stopPropagation();
	};

	return (
		<Show when={props.isOpen() && props.position() && props.socketName() && props.nodeUuid()}>
			{(() => {
				// Handle wheel event every time Show content mounts

				onMount(() => {
					contentRef.addEventListener("wheel", preventScroll, { passive: false });
				});

				onCleanup(() => {
					contentRef.removeEventListener("wheel", preventScroll);
				});

				return (
					<div
						style={{
							position: "absolute",
							left: `${props.position().left}px`,
							top: `${props.position().top}px`,
							"z-index": "1000"
						}}
					>
						<Menu.Root
							open={props.isOpen()}
							onOpenChange={(details) => {
								props.setIsOpen(details.open);
							}}
							onFocusOutside={(e) => {
								e.preventDefault();
							}}
							unmountOnExit={true}
						>
							<Menu.Trigger asChild={(p) => <InvisibleTrigger {...p()} />} />
							<Menu.Positioner>
								<Menu.Content
									ref={contentRef!}
									class="max-h-80 min-w-48 touch-none overflow-y-auto rounded-2xl"
								>
									<div class="p-1">
										<Input
											ref={inputRef!}
											placeholder="Search options..."
											value={props.searchQuery()}
											on:keydown={(e) => {
												if (!MenuNavigationEvents.has(e.code)) {
													return;
												}
												contentRef!.dispatchEvent(
													new KeyboardEvent(e.type, e)
												);
											}}
											size="medium"
											onInput={(e) =>
												props.onSearchInput(e.currentTarget.value)
											}
											class="rounded-lg border-none bg-background-gray-light text-xs outline-none"
										/>
									</div>
									<Menu.Separator class="mb-2" />
									<Switch>
										<Match when={props.searchQuery().length > 0}>
											<Show
												when={props.actions().length > 0}
												fallback={
													<div class="px-2 py-1 text-text-secondary text-xs">
														No matching options found
													</div>
												}
											>
												<For each={props.actions()}>
													{(item) => (
														<Menu.Item
															data-test={item.id}
															onSelect={() => item.onSelect?.(item)}
															value={item.id}
															disabled={item.getIsDisabled?.()}
															class="items-center justify-between gap-1 overflow-hidden text-left"
														>
															<span>{item.label}</span>
														</Menu.Item>
													)}
												</For>
											</Show>
										</Match>
										<Match when={props.searchQuery().length === 0}>
											<For each={props.actions()}>
												{(item) => (
													<Menu.Item
														data-test={item.id}
														onSelect={() => item.onSelect?.(item)}
														value={item.id}
														disabled={item.getIsDisabled?.()}
														class="items-center justify-between gap-1 overflow-hidden text-left"
													>
														<span>{item.label}</span>
													</Menu.Item>
												)}
											</For>
										</Match>
									</Switch>
								</Menu.Content>
							</Menu.Positioner>
						</Menu.Root>
					</div>
				);
			})()}
		</Show>
	);
};

export const EnumContextMenuPalette = () => {
	const {
		isOpen,
		setIsOpen,
		position,
		socketName,
		nodeUuid,
		searchQuery,
		onSearchInput,
		actions
	} = useEnumContextMenu();

	return (
		<EnumContextMenu
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			position={position}
			socketName={socketName}
			nodeUuid={nodeUuid}
			searchQuery={searchQuery}
			onSearchInput={onSearchInput}
			actions={actions}
		/>
	);
};
